import { useMemo } from 'react';

import { transformAggregationsToTree } from '@/shared/lib/tree';
import type { AssetTreeNode } from '@/shared/lib/tree/types';

import { useProjectsExperiments } from '@/entities/experiment';
import type { ProjectInfo } from '@/entities/experiment';

import { useInitialTree } from './useTreeChildren';

const createExperimentsRootNode = (projects: ProjectInfo[]): AssetTreeNode | null => {
  if (projects.length === 0) return null;

  const projectChildren: AssetTreeNode[] = projects.map((project) => {
    const experimentChildren: AssetTreeNode[] = project.experiments.map((exp) => ({
      key: `project-exp-${exp.id}`,
      title: exp.name,
      type: 'experiment',
      isLeaf: exp.runCount === 0,
      selectable: true,
      data: {
        experimentId: String(exp.id),
        experimentName: exp.name,
        runsCount: exp.runCount,
        count: exp.runCount,
        entityType: 'experiment',
      },
    }));

    return {
      key: `project-${project.id}`,
      title: project.name,
      type: 'project',
      isLeaf: project.experiments.length === 0,
      selectable: true,
      children: experimentChildren,
      data: {
        projectId: String(project.id),
        projectName: project.name,
        entityType: 'project',
        experimentsCount: project.experiments.length,
      },
    };
  });

  const totalExperiments = projects.reduce((sum, p) => sum + p.experiments.length, 0);

  return {
    key: 'category-experiments',
    title: '실험',
    type: 'entityType',
    serviceType: 'experiment',
    isLeaf: false,
    selectable: true,
    children: projectChildren,
    data: {
      entityType: 'experiment',
      totalExperiments: totalExperiments,
    },
  };
};

export const useAssetTreeData = () => {
  const { data: initialData, isLoading: isTreeLoading } = useInitialTree();
  const { data: projectsData, isLoading: isProjectsLoading } = useProjectsExperiments();

  const treeData = useMemo(() => {
    if (!initialData?.aggregations) {
      return [];
    }

    const nodes = transformAggregationsToTree(initialData.aggregations);
    const filteredNodes = nodes.filter((n) => n.data?.entityType !== 'mlmodel');

    if (projectsData?.projects && projectsData.projects.length > 0) {
      const experimentsRoot = createExperimentsRootNode(projectsData.projects);
      if (experimentsRoot) {
        filteredNodes.push(experimentsRoot);
      }
    }

    return filteredNodes;
  }, [initialData, projectsData]);

  return {
    treeData,
    isLoading: isTreeLoading || isProjectsLoading,
    initialAggregations: initialData?.aggregations,
  };
};
